/**
 * PluginDetailPage: Detailed view of a single plugin
 */

import React from "react";
import type { PluginDetailProps } from "../types/index";

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

export default function PluginDetailPage({ plugin, onBack }: PluginDetailProps): JSX.Element {
  return (
    <div>
      <div style={{ marginBottom: "1.5rem" }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.9rem",
              backgroundColor: "#f0f0f0",
              color: "#333",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              marginBottom: "1rem",
            }}
          >
            ← Back
          </button>
        )}
        <h1 style={{ margin: "0 0 0.5rem 0" }}>{plugin.pluginName}</h1>
        <p style={{ margin: "0", color: "#666", fontSize: "1rem" }}>
          {plugin.orgOrOwner}/{plugin.repoName}
        </p>
      </div>

      <p style={{ fontSize: "1.1rem", lineHeight: "1.6", marginBottom: "1.5rem" }}>
        {plugin.description}
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem", marginBottom: "2rem" }}>
        <section>
          <h2 style={{ fontSize: "1rem", color: "#0078d4", marginBottom: "1rem" }}>Details</h2>
          <dl style={{ margin: 0 }}>
            <dt style={{ fontWeight: "bold", marginTop: "0.75rem" }}>Version</dt>
            <dd style={{ margin: "0.25rem 0 0 0", color: "#666" }}>{plugin.version}</dd>

            <dt style={{ fontWeight: "bold", marginTop: "0.75rem" }}>Source</dt>
            <dd style={{ margin: "0.25rem 0 0 0", color: "#666" }}>
              {plugin.source === "ghec" ? "GitHub Enterprise Cloud" : "GitHub Enterprise Server"}
            </dd>

            {plugin.author && (
              <>
                <dt style={{ fontWeight: "bold", marginTop: "0.75rem" }}>Author</dt>
                <dd style={{ margin: "0.25rem 0 0 0", color: "#666" }}>{plugin.author}</dd>
              </>
            )}

            {plugin.license && (
              <>
                <dt style={{ fontWeight: "bold", marginTop: "0.75rem" }}>License</dt>
                <dd style={{ margin: "0.25rem 0 0 0", color: "#666" }}>{plugin.license}</dd>
              </>
            )}
          </dl>
        </section>

        <section>
          <h2 style={{ fontSize: "1rem", color: "#0078d4", marginBottom: "1rem" }}>Repository</h2>
          <p style={{ margin: "0 0 0.75rem 0" }}>
            {isSafeUrl(plugin.repoUrl) ? (
              <a
                href={plugin.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#0078d4", textDecoration: "none" }}
              >
                {plugin.repoUrl} →
              </a>
            ) : (
              <span style={{ color: "#666" }}>{plugin.repoUrl}</span>
            )}
          </p>

          {plugin.homepage && isSafeUrl(plugin.homepage) && (
            <p>
              <a
                href={plugin.homepage}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#0078d4", textDecoration: "none" }}
              >
                Homepage →
              </a>
            </p>
          )}

          <dt style={{ fontWeight: "bold", marginTop: "1rem" }}>Last Updated</dt>
          <dd style={{ margin: "0.25rem 0 0.75rem 0", color: "#666" }}>
            {new Date(plugin.lastUpdatedAt).toLocaleDateString()}
          </dd>

          <dt style={{ fontWeight: "bold" }}>Last Commit</dt>
          <dd style={{ margin: "0.25rem 0 0 0", color: "#666", fontFamily: "monospace", fontSize: "0.85rem" }}>
            {plugin.lastCommitSha.substring(0, 8)}
          </dd>
        </section>
      </div>

      {plugin.keywords && plugin.keywords.length > 0 && (
        <section>
          <h2 style={{ fontSize: "1rem", color: "#0078d4", marginBottom: "1rem" }}>Keywords</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {plugin.keywords.map((keyword) => (
              <span
                key={keyword}
                style={{
                  backgroundColor: "#e6f0ff",
                  color: "#0078d4",
                  padding: "0.5rem 0.75rem",
                  borderRadius: "4px",
                  fontSize: "0.9rem",
                }}
              >
                {keyword}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
